import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { EntityType } from '@Enums/entityType.enum';
import { BankAccountType } from '@Enums/bankAccountType.enum';

export const useAccountFormLabels = (
    entityType: ComputedRef<EntityType | null>,
    accountType: ComputedRef<BankAccountType | null>,
) => {
    const isInvestment = computed(() => accountType.value === BankAccountType.Investment);

    const isTrustOrEstate = computed(() =>
        entityType.value === EntityType.Trust || entityType.value === EntityType.Estate
    );

    const accountHolderLabel = computed(() => {
        if (isTrustOrEstate.value && !isInvestment.value) {
            return entityType.value === EntityType.Trust ? 'Trust name' : 'Estate name';
        }
        return 'Account holder';
    });

    const accountHolderFocusMessage = computed(() => {
        if (isInvestment.value) {
            return 'This is often labeled as "Credit To" on your wiring instructions or is the name of the investment group managing the account.';
        }
        if (isTrustOrEstate.value) {
            const typeName = entityType.value === EntityType.Trust ? 'trust' : 'estate';
            return `This should be the full legal name of the ${typeName} you are using for your transfer.`;
        }
        return 'This must be the full legal name of the owner of the bank account being used for this transfer.';
    });

    const accountNumberFocusMessage = computed(() => {
        if (isInvestment.value) {
            return 'This can be found on the wiring instructions provided by your investment group.';
        }
        const shared = 'can be found on a check, on a bank statement, or by logging into the online banking system associated with the account.';
        if (isTrustOrEstate.value) {
            const typeName = entityType.value === EntityType.Trust ? 'trust' : 'estate';
            return `This should be the account number for the ${typeName} and ${shared}`;
        }
        return `This ${shared}`;
    });

    const accountBillingDescription = computed(() => {
        if (isInvestment.value) {
            return 'This is the address of the investment group or firm managing the account.';
        }
        if (isTrustOrEstate.value) {
            const typeName = entityType.value === EntityType.Trust ? 'trust' : 'estate';
            return `Please enter the billing address that the ${typeName} has on file for this bank account.`;
        }
        return 'Please use the personal address associated with the owner of the bank account.';
    });

    const finalCreditToTitle = computed(() => isInvestment.value ? 'Final credit to' : null);

    return {
        isInvestment,
        isTrustOrEstate,
        accountHolderLabel,
        accountHolderFocusMessage,
        accountNumberFocusMessage,
        accountBillingDescription,
        finalCreditToTitle,
    };
};

export default useAccountFormLabels;
